import { useMemo, useState } from 'react';
import { BackupImport } from './BackupImport';
import { mapFromText, importOutline, type MapDocument } from '@orbit-mind/domain';
import { useLocale } from '@orbit-mind/engine-react';
import { randomIds } from '@orbit-mind/map-engine';
import type { MapId, NodeId, Timestamp } from '@orbit-mind/shared-types';
import { LOCAL_AUTHOR, LOCAL_WORKSPACE } from '../engine/useOpenMap';
import { Button } from '../ui/controls';
import { importPdf } from '../account/api';
import {
  pdfMaterialText,
  previewNativeDocument,
  type NativeImportPreview,
} from '@orbit-mind/api-client';

export function TextImportPanel({
  onAccept,
  busy,
}: {
  readonly onAccept: (doc: MapDocument) => void;
  readonly busy: boolean;
}): React.JSX.Element {
  const { text: t } = useLocale();
  const [title, setTitle] = useState('');
  const [source, setSource] = useState('');
  const [text, setText] = useState('');
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfError, setPdfError] = useState('');
  const [nativePreview, setNativePreview] = useState<NativeImportPreview | null>(null);
  const [seed] = useState(() => ({
    mapId: `map_${randomIds().nodeId()}` as MapId,
    now: Date.now() as Timestamp,
  }));
  const preview = useMemo(() => {
    let index = 0;
    return mapFromText({
      title,
      source,
      text,
      mapId: seed.mapId,
      nodeId: () => `${seed.mapId}_n${String(index++)}` as NodeId,
      now: seed.now,
      ownerId: LOCAL_AUTHOR,
      workspaceId: LOCAL_WORKSPACE,
    });
  }, [title, source, text, seed]);
  const field = 'w-full rounded-xl border border-edge bg-surface px-3 py-2.5 text-ink';
  return (
    <div className="space-y-4 overflow-y-auto p-5">
      <BackupImport />
      <label className="border-edge block rounded-xl border p-3 text-sm">
        {t(
          'Open Orbit Mind JSON (offline, up to 10 MB). Creates a copy; attached files must be reattached.',
          'Abrir Orbit Mind JSON (offline, até 10 MB). Cria uma cópia; os arquivos anexados precisam ser adicionados novamente.',
        )}
        <input
          type="file"
          accept="application/json,.json"
          disabled={busy || pdfBusy}
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = '';
            if (!file) return;
            if (file.size > 10 * 1024 * 1024) {
              setPdfError('Limite: 10 MB.');
              return;
            }
            setPdfBusy(true);
            setPdfError('');
            void file
              .text()
              .then((text) => {
                const result = previewNativeDocument(text, {
                  ...seed,
                  ownerId: LOCAL_AUTHOR,
                  workspaceId: LOCAL_WORKSPACE,
                });
                if (result.ok) setNativePreview(result.value);
                else setPdfError(result.error.message);
              })
              .catch(() => {
                setPdfError('Não foi possível ler o arquivo.');
              })
              .finally(() => {
                setPdfBusy(false);
              });
          }}
        />
      </label>
      {nativePreview && (
        <div
          className="bg-accent-softer space-y-3 rounded-xl p-4"
          data-testid="native-import-preview"
        >
          <strong>
            {t('Preview before importing', 'Prévia antes de importar')}:{' '}
            {nativePreview.document.map.title}
          </strong>
          <p>
            {Object.keys(nativePreview.document.nodes).length}{' '}
            {t(
              'topics; hierarchy, notes and styles preserved.',
              'tópicos; hierarquia, notas e estilos preservados.',
            )}
          </p>
          <p>
            {nativePreview.removedFiles} {t('file references', 'referências a arquivos')} ·{' '}
            {nativePreview.removedMedia}{' '}
            {t(
              'media items need reattaching. Use a backup to include files.',
              'itens de mídia precisam ser anexados novamente. Use um backup para incluir arquivos.',
            )}
          </p>
          <ul>
            {importOutline(nativePreview.document, 15).map((row) => (
              <li key={row.id} style={{ paddingLeft: Math.min(row.depth, 8) * 12 }}>
                {row.text}
              </li>
            ))}
          </ul>
          <Button
            tone="primary"
            disabled={busy || pdfBusy}
            onClick={() => {
              onAccept(nativePreview.document);
            }}
          >
            {t('Import this copy', 'Importar esta cópia')}
          </Button>
          <Button
            disabled={busy}
            onClick={() => {
              setNativePreview(null);
            }}
          >
            {t('Cancel', 'Cancelar')}
          </Button>
        </div>
      )}
      <label className="border-edge block rounded-xl border p-3 text-sm">
        {t('Open TXT or Markdown · offline', 'Abrir TXT ou Markdown · offline')}
        <input
          type="file"
          accept=".txt,.md,.markdown,text/plain,text/markdown"
          disabled={busy || pdfBusy}
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = '';
            if (!file) return;
            if (file.size > 200_000) {
              setPdfError(
                t('Text limit: 50,000 characters.', 'Limite do texto: 50.000 caracteres.'),
              );
              return;
            }
            setPdfBusy(true);
            setPdfError('');
            void file
              .text()
              .then((material) => {
                if (material.length > 50_000)
                  throw Error(
                    t('Text limit: 50,000 characters.', 'Limite do texto: 50.000 caracteres.'),
                  );
                setNativePreview(null);
                setText(material);
                setTitle(file.name.replace(/\.(txt|md|markdown)$/i, ''));
                setSource(file.name);
              })
              .catch(() => {
                setPdfError(
                  t('Could not read the text file.', 'Não foi possível ler o arquivo de texto.'),
                );
              })
              .finally(() => {
                setPdfBusy(false);
              });
          }}
        />
      </label>
      <label className="border-edge block rounded-xl border p-3 text-sm">
        {t(
          'Import PDF (online, signed in). Sent only for text extraction, without AI or permanent storage. Up to 5 MB / 100 pages.',
          'Importar PDF (online, com conta). Enviado apenas para extrair texto, sem IA nem armazenamento permanente. Até 5 MB / 100 páginas.',
        )}
        <input
          type="file"
          accept="application/pdf,.pdf"
          disabled={busy || pdfBusy}
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = '';
            if (file === undefined) return;
            if (file.size > 5 * 1024 * 1024) {
              setPdfError(t('PDF limit: 5 MB.', 'Limite do PDF: 5 MB.'));
              return;
            }
            setPdfBusy(true);
            setPdfError('');
            void importPdf(file).then((result) => {
              setPdfBusy(false);
              if (!result.ok) {
                setPdfError(result.error.message);
                return;
              }
              setNativePreview(null);
              setSource(file.name);
              setTitle(file.name.replace(/\.pdf$/i, ''));
              setText(pdfMaterialText(result.value));
            });
          }}
        />
      </label>
      {pdfBusy && <p role="status">{t('Reading file…', 'Lendo arquivo…')}</p>}
      {pdfError !== '' && <p role="alert">{pdfError}</p>}
      <p className="text-ink-muted text-sm">
        {t(
          'Turn your notes into a map. Use # headings for branches and one idea per line. Original passages stay in each node’s notes. Works offline.',
          'Transforme suas anotações em mapa. Use títulos com # para criar ramos e uma ideia por linha. Os trechos originais ficam nas notas de cada nó. Funciona offline.',
        )}
      </p>
      <label className="block text-sm">
        {t('Map title', 'Título do mapa')}
        <input
          className={field}
          maxLength={500}
          value={title}
          onChange={(event) => {
            setTitle(event.target.value);
          }}
        />
      </label>
      <label className="block text-sm">
        {t('Source name or link (optional)', 'Nome ou link da fonte (opcional)')}
        <input
          className={field}
          maxLength={1000}
          value={source}
          onChange={(event) => {
            setSource(event.target.value);
          }}
        />
      </label>
      <label className="block text-sm">
        {t('Your material', 'Seu material')}
        <textarea
          className={`${field} min-h-40`}
          value={text}
          maxLength={50001}
          onChange={(event) => {
            setText(event.target.value);
          }}
          placeholder={t(
            '# Topic\nAn important idea\nAnother idea',
            '# Tema\nUma ideia importante\nOutra ideia',
          )}
        />
      </label>
      <p className="text-ink-muted text-xs">
        {text.length.toLocaleString()} / 50.000 ·{' '}
        {t(
          'Up to 200 lines. No content is sent to AI.',
          'Até 200 linhas. Nenhum conteúdo é enviado à IA.',
        )}
      </p>
      {preview.ok ? (
        <div className="bg-accent-softer rounded-xl p-4">
          <strong>
            {t('Preview', 'Prévia')}: {Object.keys(preview.value.nodes).length}{' '}
            {t('ideas', 'ideias')}
          </strong>
          <ul className="mt-2 space-y-1 text-sm">
            {importOutline(preview.value).map((node) => (
              <li
                key={node.id}
                className="truncate"
                style={{ paddingLeft: Math.min(node.depth, 8) * 12 }}
              >
                {node.text}
              </li>
            ))}
          </ul>
        </div>
      ) : text.length > 0 && title.length > 0 ? (
        <p role="alert">
          {t(
            'Check the title and use at most 50,000 characters and 200 non-empty lines.',
            'Verifique o título e use no máximo 50.000 caracteres e 200 linhas preenchidas.',
          )}
        </p>
      ) : null}
      {preview.ok && (
        <p className="text-ink-muted text-xs">
          {t(
            'Headings and indented branches are preserved. Original lines remain in notes. Images, links and text formatting are not extracted; review before creating.',
            'Títulos e ramos com recuo são preservados. Linhas originais ficam nas notas. Imagens, links e formatação não são extraídos; confira antes de criar.',
          )}
        </p>
      )}
      <Button
        tone="primary"
        disabled={busy || pdfBusy || !preview.ok}
        onClick={() => {
          if (preview.ok) onAccept(preview.value);
        }}
      >
        {t('Create this map', 'Criar este mapa')}
      </Button>
    </div>
  );
}
